/*
 * TopNav (Figma 119:44907): height 64, --surface-header, bottom 1px --border-strong (drawn above the
 * items, as Figma strokes are), padding-x 32px, space-between.
 * Left: gap 32 — tenant logo (Figma: 129 × 12 wordmark) and the menu.
 * Item: height 64, padding-x 16px, gap 4; label 550 16/24 --text-default; dropdown caret 12px.
 * Current item: --surface-default fill, 1px --border-strong left/right (in the layout: the item is 2px
 * wider, as the overlay against Figma shows), --text-link label.
 * Not in the design: hover (--interaction-hover) and keyboard focus (Focus M, inset).
 * Right: the DEMO badge on sales demos (small, not in Figma), then the user button: the avatar only.
 *
 * Narrow screens (not in Figma; breakpoints in tokens.css): below 1280 the padding is the page
 * gutter (24px) and items get 12px sides. Below 1024 the views collapse into a menu button left of
 * the logo (40×40, the ghost IconButton look); it opens a panel under the bar, over a scrim, with
 * one 48px row per view (the current one in --text-link with a 3px --accent-default bar). The
 * panel takes focus (the current view's link), Tab and Shift+Tab stay inside it, ↑ / ↓ / Home /
 * End move between the links, Esc or a click outside closes it and focus returns to the button;
 * choosing a view closes it. The DEMO badge and the avatar menu stay in the bar.
 *
 * Colours come from the --nav-* tokens (tokens.css), which default to the values above, so
 * another app can recolour the bar: the admin panel's blue bar (src/admin/theme.css). `brand`
 * replaces the tenant logo. An entry with `href` is a plain link out of the app (the old
 * panel's pages). A group whose items carry a `group` or `description` opens as a wide panel:
 * one column per group, each item an icon, its label and a line under it (admin menus).
 */
import { useEffect, useId, useRef, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link, NavLink, useLocation } from 'react-router'
import { cx } from '@/kit/cx'
import { Icon } from '@/kit/Icon'
import { check16Menu } from '@/kit/icons'
import { Avatar } from './Avatar'
import { DemoTag, TenantMark } from './TenantMark'

export interface NavEntry {
    key: string
    label: string
    /** A route of this app. */
    to?: string
    /** A plain link out of the app (a full page load), e.g. a page of the old panel. */
    href?: string
    children?: NavEntry[]
    /** In a wide menu panel: the column the item sits in. */
    group?: string
    /** In a wide menu panel: the line under the label (e.g. a live count). */
    description?: string
    /** `danger` colours the description (something failed). */
    descriptionTone?: 'danger'
    /** In a wide menu panel: a 16px icon (an svg URL, see kit/icons). */
    icon?: string
    /**
     * Whether it is the current entry, regardless of the address: an ERP app (modules/erp/nav.ts),
     * whose screens live under paths of their own. Without it an entry is current at its own path.
     */
    current?: boolean
}

const ITEM =
    'relative flex h-[64px] shrink-0 cursor-pointer items-center gap-[4px] px-[16px] max-xl:px-[12px] text-[16px] leading-[24px] font-[550] whitespace-nowrap no-underline ' +
    'focus-visible:outline-none'
// z-20: the current tab paints over the header's bottom border, so it opens into the page.
const CURRENT =
    'z-20 border-x border-solid border-[var(--nav-rule)] bg-[var(--nav-current-surface)] text-[var(--nav-current-text)] focus-visible:shadow-[var(--focus-inset)]'
const IDLE = 'text-[var(--nav-text)] hover:bg-[var(--nav-hover)] focus-visible:shadow-[var(--nav-focus-inset)]'
// A group whose view is shown: as CURRENT, but its fill is its own token (none on tenant bars).
const CURRENT_GROUP =
    'z-20 border-x border-solid border-[var(--nav-rule)] bg-[var(--nav-current-group-surface)] text-[var(--nav-current-text)] focus-visible:shadow-[var(--focus-inset)]'

/** A wide menu panel: does any of the group's items ask for one? */
function isPanel(entry: NavEntry): boolean {
    return entry.children?.some((child) => child.group !== undefined || child.description !== undefined) ?? false
}

/** The items of a wide panel, by column, in the order the columns first appear. */
function columns(children: NavEntry[]): { title: string; items: NavEntry[] }[] {
    const byGroup = new Map<string, NavEntry[]>()
    for (const child of children) {
        const title = child.group ?? ''
        byGroup.set(title, [...(byGroup.get(title) ?? []), child])
    }
    return [...byGroup].map(([title, items]) => ({ title, items }))
}

const PANEL_ITEM =
    'flex items-start gap-[10px] rounded-[8px] p-[8px] text-left no-underline text-[var(--text-default)] hover:bg-[var(--interaction-hover)] focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none'

/** One item of a wide panel: icon tile, label, and the line under it. */
function PanelItem({ entry, here, onChoose }: { entry: NavEntry; here: boolean; onChoose: () => void }) {
    const body = (
        <>
            <span aria-hidden className="flex size-[32px] shrink-0 items-center justify-center rounded-[6px] bg-[var(--surface-page)]">
                {entry.icon && <Icon src={entry.icon} size={16} />}
            </span>
            <span className="flex min-w-0 flex-col">
                <span className={cx('text-[14px] leading-[20px] font-[550]', here && 'text-[var(--text-link)]')}>{entry.label}</span>
                {entry.description && (
                    <span className={cx('text-[13px] leading-[18px] font-normal', entry.descriptionTone === 'danger' ? 'text-[var(--text-danger)]' : 'text-[var(--text-weak)]')}>
                        {entry.description}
                    </span>
                )}
            </span>
        </>
    )
    return entry.href ? (
        <a role="menuitem" href={entry.href} onClick={onChoose} className={PANEL_ITEM}>
            {body}
        </a>
    ) : (
        <NavLink role="menuitem" to={entry.to ?? '/'} aria-current={here ? 'page' : undefined} onClick={onChoose} className={PANEL_ITEM}>
            {body}
        </NavLink>
    )
}

/** Whether `to` is the page shown (or a page under it). */
function isCurrent(to: string | undefined, pathname: string): boolean {
    return Boolean(to) && (pathname === to || pathname.startsWith(`${to}/`))
}

/**
 * A menu group (an extension's `section`): the item opens a popout with the group's views, in the
 * user menu's look. While one of them is shown the item looks current and that view has a check.
 * Keys: ↓ / Enter / Space open it on its first view, ↑ / ↓ move, Esc closes and returns focus.
 */
function Dropdown({ entry }: { entry: NavEntry }) {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const menuId = useId()
    const { pathname } = useLocation()
    const current = entry.children?.some((child) => isCurrent(child.to, pathname)) ?? false
    useEffect(() => {
        if (!open) return
        const close = (event: PointerEvent) => {
            if (!ref.current?.contains(event.target as Node)) setOpen(false)
        }
        document.addEventListener('pointerdown', close)
        return () => document.removeEventListener('pointerdown', close)
    }, [open])
    useEffect(() => {
        if (open) ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    }, [open])
    function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
        if (event.key === 'Escape' && open) {
            event.preventDefault()
            setOpen(false)
            buttonRef.current?.focus()
            return
        }
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
        event.preventDefault()
        if (!open) {
            setOpen(true)
            return
        }
        const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])
        const at = items.indexOf(document.activeElement as HTMLElement)
        items[(at + (event.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length]?.focus()
    }
    return (
        <div ref={ref} className="relative h-full" onKeyDown={onKeyDown}>
            <button
                ref={buttonRef}
                type="button"
                aria-expanded={open}
                aria-haspopup="menu"
                aria-controls={open ? menuId : undefined}
                className={cx(ITEM, 'border-y-0', current ? CURRENT_GROUP : cx(IDLE, 'border-x-0', open ? 'bg-[var(--nav-expanded)]' : 'bg-transparent'))}
                onClick={() => setOpen((o) => !o)}
            >
                {entry.label}
                {/* Figma's caret-down-12, its stroke a token so another bar can recolour it. */}
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" className="block shrink-0">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke={current ? 'var(--nav-current-caret)' : 'var(--nav-caret)'} strokeWidth="2" strokeLinecap="round" />
                </svg>
            </button>
            {open && isPanel(entry) && (
                <div
                    id={menuId}
                    role="menu"
                    aria-label={entry.label}
                    data-testid={`nav-panel-${entry.key}`}
                    className="absolute top-[calc(100%+8px)] left-0 z-30 grid w-max max-w-[calc(100vw-32px)] gap-[4px] overflow-clip rounded-[12px] border border-solid border-[var(--border-default)] bg-[var(--surface-raised)] p-[8px] shadow-[var(--shadow-dialog)]"
                    style={{ gridTemplateColumns: `repeat(${columns(entry.children ?? []).length}, minmax(0, 232px))` }}
                >
                    {columns(entry.children ?? []).map((column) => (
                        <div key={column.title} role="group" aria-label={column.title || entry.label} className="flex min-w-0 flex-col gap-[2px]">
                            {column.title && <p className="m-0 px-[8px] pt-[8px] pb-[4px] text-[12px] leading-[16px] font-semibold text-[var(--text-weaker)]">{column.title}</p>}
                            {column.items.map((child) => (
                                <PanelItem key={child.key} entry={child} here={isCurrent(child.to, pathname)} onChoose={() => setOpen(false)} />
                            ))}
                        </div>
                    ))}
                </div>
            )}
            {open && !isPanel(entry) && (
                <div
                    id={menuId}
                    role="menu"
                    aria-label={entry.label}
                    className="absolute top-[calc(100%+8px)] left-0 z-30 flex min-w-[220px] flex-col overflow-clip rounded-[8px] bg-[var(--surface-raised)] p-[4px] shadow-[var(--shadow-popout)]"
                >
                    {entry.children?.map((child) => {
                        const here = isCurrent(child.to, pathname)
                        return child.href ? (
                            <a key={child.key} role="menuitem" href={child.href} onClick={() => setOpen(false)} className={cx(MENU_ITEM, 'no-underline')}>
                                {child.label}
                            </a>
                        ) : (
                            <NavLink
                                key={child.key}
                                role="menuitem"
                                to={child.to ?? '#'}
                                aria-current={here ? 'page' : undefined}
                                onClick={() => setOpen(false)}
                                className={cx(MENU_ITEM, 'no-underline', here && 'text-[var(--text-link)]')}
                            >
                                {child.label}
                                {here && <Icon src={check16Menu} size={16} />}
                            </NavLink>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

/** Phosphor "List" (16px), inline like the Overview's arrow: the kit has no menu icon yet. */
function ListIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M2.5 4h11M2.5 8h11M2.5 12h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    )
}

/** Phosphor "X" (16px), for the open menu's button. */
function CloseIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M12.5 3.5l-9 9M12.5 12.5l-9-9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    )
}

const MENU_LINK =
    'relative flex min-h-[48px] w-full items-center px-[var(--layout-page-padding-x)] text-[16px] leading-[24px] font-[550] no-underline ' +
    'focus-visible:shadow-[var(--focus-inset)] focus-visible:outline-none'
const MENU_CURRENT =
    "text-[var(--text-link)] before:absolute before:top-[12px] before:bottom-[12px] before:left-0 before:w-[3px] before:rounded-r-[2px] before:bg-[var(--accent-default)] before:content-['']"

/** The views as a menu, below 1024px: a button that opens a panel under the bar (see the header). */
function MobileMenu({ entries, label }: { entries: NavEntry[]; label: string }) {
    const [open, setOpen] = useState(false)
    const panelId = useId()
    const button = useRef<HTMLButtonElement>(null)
    const panel = useRef<HTMLDivElement>(null)
    const scrim = useRef<HTMLDivElement>(null)
    const location = useLocation()

    // Any navigation (choosing a view, Back) closes the menu.
    const [path, setPath] = useState(location.pathname)
    if (path !== location.pathname) {
        setPath(location.pathname)
        setOpen(false)
    }

    // Focus into the panel: the current view, else the first one.
    useEffect(() => {
        if (!open) return
        const links = Array.from(panel.current?.querySelectorAll<HTMLAnchorElement>('a[href]') ?? [])
        ;(links.find((a) => a.getAttribute('aria-current') === 'page') ?? links[0])?.focus()
    }, [open])

    // Esc anywhere closes it (focus back on the button); so does a press elsewhere in the bar. The
    // scrim closes it on click, not on press: closing on press would drop the scrim before the
    // tap's click, which would then land on the page underneath.
    useEffect(() => {
        if (!open) return
        const onKey = (event: globalThis.KeyboardEvent) => {
            if (event.key !== 'Escape' || event.defaultPrevented) return
            event.preventDefault()
            setOpen(false)
            button.current?.focus()
        }
        const onPointer = (event: PointerEvent) => {
            const target = event.target as Node
            if (!panel.current?.contains(target) && !button.current?.contains(target) && !scrim.current?.contains(target)) setOpen(false)
        }
        document.addEventListener('keydown', onKey)
        document.addEventListener('pointerdown', onPointer)
        return () => {
            document.removeEventListener('keydown', onKey)
            document.removeEventListener('pointerdown', onPointer)
        }
    }, [open])

    const onPanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const links = Array.from(event.currentTarget.querySelectorAll<HTMLAnchorElement>('a[href]'))
        if (links.length === 0) return
        const index = links.indexOf(document.activeElement as HTMLAnchorElement)
        const move = (to: number) => {
            event.preventDefault()
            links[(to + links.length) % links.length]!.focus()
        }
        if (event.key === 'ArrowDown') move(index + 1)
        else if (event.key === 'ArrowUp') move(index - 1)
        else if (event.key === 'Home') move(0)
        else if (event.key === 'End') move(links.length - 1)
        // Tab stays in the panel while it is open.
        else if (event.key === 'Tab' && !event.shiftKey && index === links.length - 1) move(0)
        else if (event.key === 'Tab' && event.shiftKey && index <= 0) move(links.length - 1)
    }

    const link = (entry: NavEntry, nested = false) => (
        <li key={entry.key}>
            {entry.href ? (
                <a
                    href={entry.href}
                    onClick={() => setOpen(false)}
                    className={cx(MENU_LINK, nested && 'pl-[calc(var(--layout-page-padding-x)+16px)]', 'text-[var(--text-default)] hover:bg-[var(--interaction-hover)]')}
                >
                    {entry.label}
                </a>
            ) : entry.current !== undefined ? (
                <Link
                    to={entry.to ?? '/'}
                    onClick={() => setOpen(false)}
                    aria-current={entry.current ? 'page' : undefined}
                    className={cx(MENU_LINK, nested && 'pl-[calc(var(--layout-page-padding-x)+16px)]', entry.current ? MENU_CURRENT : 'text-[var(--text-default)] hover:bg-[var(--interaction-hover)]')}
                >
                    {entry.label}
                </Link>
            ) : (
                <NavLink
                    to={entry.to ?? '/'}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                        cx(MENU_LINK, nested && 'pl-[calc(var(--layout-page-padding-x)+16px)]', isActive ? MENU_CURRENT : 'text-[var(--text-default)] hover:bg-[var(--interaction-hover)]')
                    }
                >
                    {entry.label}
                </NavLink>
            )}
        </li>
    )

    return (
        <>
            <button
                ref={button}
                type="button"
                aria-label={label}
                aria-expanded={open}
                aria-controls={open ? panelId : undefined}
                onClick={() => setOpen((o) => !o)}
                className={cx(
                    'flex size-[var(--size-touch-target)] shrink-0 cursor-pointer items-center justify-center rounded-[6px] border-0 p-0 text-[var(--nav-text)]',
                    'hover:bg-[var(--nav-hover)] focus-visible:shadow-[var(--nav-focus)] focus-visible:outline-none',
                    open ? 'bg-[var(--nav-menu-open)]' : 'bg-transparent',
                )}
            >
                {open ? <CloseIcon /> : <ListIcon />}
            </button>
            {open && (
                <>
                    {/* The scrim sits under the bar (the bar lifts itself above it while open: data-nav-open). */}
                    {createPortal(<div ref={scrim} aria-hidden className="fixed inset-0 z-40 touch-none bg-[var(--overlay)]" onClick={() => setOpen(false)} />, document.body)}
                    <div
                        ref={panel}
                        id={panelId}
                        data-nav-open
                        onKeyDown={onPanelKeyDown}
                        className="absolute inset-x-0 top-[64px] z-50 max-h-[calc(100dvh-64px)] overflow-y-auto border-b border-solid border-[var(--border-strong)] bg-[var(--surface-default)] py-[8px] shadow-[var(--shadow-popout)]"
                    >
                        <nav aria-label={label}>
                            <ul className="m-0 flex list-none flex-col p-0">
                                {entries.map((entry) =>
                                    entry.children?.length ? (
                                        <li key={entry.key}>
                                            <p className="m-0 px-[var(--layout-page-padding-x)] pt-[12px] pb-[4px] text-[14px] leading-[20px] font-[550] text-[var(--text-weak)]">{entry.label}</p>
                                            <ul className="m-0 flex list-none flex-col p-0">{entry.children.map((child) => link(child, true))}</ul>
                                        </li>
                                    ) : (
                                        link(entry)
                                    ),
                                )}
                            </ul>
                        </nav>
                    </div>
                </>
            )}
        </>
    )
}

export interface LanguageOption {
    code: string
    /** The language's own name ("Deutsch"), so anyone can find theirs. */
    label: string
}

const MENU_ITEM =
    'ss03-ss04 flex w-full cursor-pointer items-center justify-between gap-[8px] rounded-[8px] border-0 bg-transparent px-[8px] py-[6px] pointer-coarse:py-[10px] text-left text-[14px] leading-[20px] font-normal text-[var(--text-default)] hover:bg-[var(--interaction-hover)]'

function UserMenu({
    userName,
    menuLabel,
    signOutLabel,
    onSignOut,
    languages,
    language,
    languageLabel,
    onLanguage,
    versionLabel,
    menuLinks,
}: {
    versionLabel?: string | null
    menuLinks?: NavEntry[]
    userName: string
    menuLabel: string
    signOutLabel?: string
    onSignOut?: () => void
    languages?: LanguageOption[]
    language?: string
    languageLabel?: string
    onLanguage?: (code: string) => void
}) {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (!open) return
        const close = (event: PointerEvent) => {
            if (!ref.current?.contains(event.target as Node)) setOpen(false)
        }
        document.addEventListener('pointerdown', close)
        return () => document.removeEventListener('pointerdown', close)
    }, [open])
    return (
        <div ref={ref} className="relative flex items-center">
            <button
                type="button"
                aria-label={menuLabel}
                aria-expanded={open}
                aria-haspopup="menu"
                title={userName}
                className="flex cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 focus-visible:shadow-[var(--nav-focus)] focus-visible:outline-none pointer-coarse:size-[var(--size-touch-target)]"
                onClick={() => setOpen((o) => !o)}
            >
                <Avatar name={userName} />
            </button>
            {open && (
                <div
                    role="menu"
                    className="absolute top-[calc(100%+8px)] right-0 z-30 flex min-w-[220px] flex-col overflow-clip rounded-[8px] bg-[var(--surface-raised)] shadow-[var(--shadow-popout)]"
                >
                    <p className="m-0 px-[8px] pt-[8px] pb-[4px] text-[14px] leading-[20px] font-[550] text-[var(--text-default)]">{userName}</p>
                    {menuLinks && menuLinks.length > 0 && (
                        <div className="flex flex-col border-b border-solid border-[var(--border-default)] pb-[4px]">
                            {menuLinks.map((link) =>
                                link.href ? (
                                    <a key={link.key} href={link.href} role="menuitem" onClick={() => setOpen(false)} className={cx(MENU_ITEM, 'no-underline')}>
                                        {link.label}
                                    </a>
                                ) : (
                                    <NavLink key={link.key} to={link.to ?? '/'} role="menuitem" onClick={() => setOpen(false)} className={cx(MENU_ITEM, 'no-underline')}>
                                        {link.label}
                                    </NavLink>
                                ),
                            )}
                        </div>
                    )}
                    {languages && languages.length > 1 && onLanguage && (
                        <div role="group" aria-label={languageLabel} className="flex flex-col border-b border-solid border-[var(--border-default)] pb-[4px]">
                            {languages.map((option) => (
                                <button
                                    key={option.code}
                                    type="button"
                                    role="menuitemradio"
                                    aria-checked={option.code === language}
                                    lang={option.code}
                                    onClick={() => {
                                        onLanguage(option.code)
                                        setOpen(false)
                                    }}
                                    className={MENU_ITEM}
                                >
                                    {option.label}
                                    {option.code === language && <Icon src={check16Menu} size={16} />}
                                </button>
                            ))}
                        </div>
                    )}
                    {onSignOut && (
                        <button type="button" role="menuitem" onClick={onSignOut} className={MENU_ITEM}>
                            {signOutLabel}
                        </button>
                    )}
                    {versionLabel && (
                        <p className="m-0 border-t border-solid border-[var(--border-default)] px-[8px] pt-[6px] pb-[8px] text-[12px] leading-[16px] font-normal text-[var(--text-weaker)]">
                            {versionLabel}
                        </p>
                    )}
                </div>
            )}
        </div>
    )
}

export function TopNav({
    logoUrl,
    tenantName,
    logoHeight,
    demoLabel,
    demoHint,
    languages,
    language,
    languageLabel,
    onLanguage,
    versionLabel,
    menuLinks,
    entries,
    userName,
    menuLabel,
    signOutLabel,
    onSignOut,
    navMenuLabel,
    brand,
}: {
    logoUrl?: string
    tenantName: string
    logoHeight?: number | null
    /** Set on sales demos: a badge next to the user button; the hint is its tooltip. */
    demoLabel?: string | null
    demoHint?: string | null
    languages?: LanguageOption[]
    language?: string
    languageLabel?: string
    onLanguage?: (code: string) => void
    /** Shown small at the bottom of the user menu (support: which build is this tab running). */
    versionLabel?: string | null
    /** Extension views listed in the user menu (e.g. a product tour), above the language. */
    menuLinks?: NavEntry[]
    entries: NavEntry[]
    userName: string
    menuLabel: string
    /** Without `onSignOut` the user menu has no sign-out (the admin panel signs in with the browser). */
    signOutLabel?: string
    onSignOut?: () => void
    /** Accessible name of the views menu button (below 1024px). */
    navMenuLabel?: string
    /** Shown instead of the tenant logo (the admin panel's mark); `tenantName` stays its accessible name. */
    brand?: ReactNode
}) {
    return (
        // While the views menu is open the bar sits above its scrim (z-40).
        <header className="relative flex h-[64px] w-full items-center justify-between gap-[12px] bg-[var(--nav-surface)] px-[var(--layout-page-padding-x)] has-[[data-nav-open]]:z-50">
            <div className="flex h-full min-w-0 items-center gap-[32px] max-xl:gap-[24px] max-lg:gap-[8px]">
                {entries.length > 0 && (
                    <div className="-ml-[8px] flex lg:hidden">
                        <MobileMenu entries={entries} label={navMenuLabel ?? 'Menu'} />
                    </div>
                )}
                {/* The logo goes where the first menu item goes (the landing view). */}
                <NavLink
                    to={entries[0]?.to ?? '/'}
                    aria-label={tenantName}
                    className="flex min-h-[40px] shrink-0 items-center rounded-[4px] no-underline focus-visible:shadow-[var(--nav-focus)] focus-visible:outline-none"
                >
                    {brand ?? <TenantMark logoUrl={logoUrl} name={tenantName} logoHeight={logoHeight} />}
                </NavLink>
                <nav aria-label="main" className="flex h-full items-center max-lg:hidden">
                    {entries.map((entry) =>
                        entry.children?.length ? (
                            <Dropdown key={entry.key} entry={entry} />
                        ) : entry.href ? (
                            <a key={entry.key} href={entry.href} className={cx(ITEM, IDLE)}>
                                {entry.label}
                            </a>
                        ) : entry.current !== undefined ? (
                            <Link key={entry.key} to={entry.to ?? '/'} aria-current={entry.current ? 'page' : undefined} className={cx(ITEM, entry.current ? CURRENT : IDLE)}>
                                {entry.label}
                            </Link>
                        ) : (
                            <NavLink key={entry.key} to={entry.to ?? '/'} className={({ isActive }) => cx(ITEM, isActive ? CURRENT : IDLE)}>
                                {entry.label}
                            </NavLink>
                        ),
                    )}
                </nav>
            </div>
            <div className="flex shrink-0 items-center gap-[12px]">
                {demoLabel && <DemoTag label={demoLabel} hint={demoHint} />}
                <UserMenu
                    userName={userName}
                    menuLabel={menuLabel}
                    signOutLabel={signOutLabel}
                    onSignOut={onSignOut}
                    languages={languages}
                    language={language}
                    languageLabel={languageLabel}
                    onLanguage={onLanguage}
                    versionLabel={versionLabel}
                    menuLinks={menuLinks}
                />
            </div>
            {/* Bottom border above the items, like Figma's frame stroke. */}
            <span aria-hidden className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 h-px bg-[var(--nav-rule)]" />
        </header>
    )
}
